import {
  Box,
  Divider,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tr
} from '@chakra-ui/react';
import { IVendorHistoryResponse } from '@publishing-engine/domain/models/vendor-exports';
import { useQuery } from '@tanstack/react-query';
import { fetchSubmissionContext } from 'src/api/queries';
import { formatDateAndTime } from '../../utils/date';

interface VendorExportHistoryDetailProps {
  selectedRow: IVendorHistoryResponse['data'][number];
}

const renderValues = (heading: string, value: string) => {
  return (
    <Flex gap={2} width={{ base: '100%', md: '50%' }}>
      <Text textStyle={'headingLine'}>{heading + ': '}</Text>
      <Text textStyle={'textContext'}>{value}</Text>
    </Flex>
  );
};

const renderServerInfoTable = (
  data: Record<string, any>,
  hiddenKeys: string[]
) => {
  if (!data || typeof data !== 'object') {
    return <Text>No data available</Text>;
  }

  return (
    <TableContainer>
      <Table size="sm" variant="simple">
        <Tbody>
          {Object.entries(data)
            .filter(
              ([key, value]) =>
                value !== null &&
                value !== undefined &&
                !hiddenKeys.includes(key)
            )
            .map(([key, value]) => (
              <Tr key={key}>
                <Td fontWeight="semibold" whiteSpace="nowrap">
                  {key}
                </Td>
                <Td wordBreak="break-all">
                  {typeof value === 'object'
                    ? JSON.stringify(value, null, 2)
                    : String(value)}
                </Td>
              </Tr>
            ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

const VendorExportHistoryDetail = ({
  selectedRow
}: VendorExportHistoryDetailProps) => {
  const { data: journal } = useQuery({
    queryKey: ['submission-context', selectedRow.submissionContextId],
    queryFn: () => fetchSubmissionContext(selectedRow.submissionContextId),
    enabled: !!selectedRow.submissionContextId
  });
  return (
    <Box p={{ base: 3, md: 5 }}>
      <Text textStyle={'headingGroup'} mb={2}>
        Export Details
      </Text>
      <Divider />
      <Flex direction={'column'} mt={2}>
        <Flex justifyContent={'space-between'} flexDirection={{ base: 'column', md: 'row' }}>
          {renderValues('Journal Name', journal?.data.name!)}
          {renderValues('Manuscript ID', selectedRow.manuscriptId)}
        </Flex>
        <Flex justifyContent={'space-between'} flexDirection={{ base: 'column', md: 'row' }}>
          {renderValues(
            'Exported To',
            selectedRow.event.exportConfigType.toLocaleUpperCase()
          )}
          {renderValues(
            'Export Type',
            selectedRow.event.exportType === 'full'
              ? 'Package Export'
              : 'Metadata Export'
          )}
        </Flex>
        <Flex justifyContent={'space-between'} flexDirection={{ base: 'column', md: 'row' }}>
          {renderValues('Event Type', selectedRow.event.triggeredBy)}
          {renderValues(
            'Date',
            formatDateAndTime(selectedRow.date)
          )}
        </Flex>
        <Flex justifyContent={'space-between'} flexDirection={{ base: 'column', md: 'row' }}>
          {renderValues(
            'Status',
            selectedRow.event.status === 'complete'
              ? 'SUCCESS'
              : selectedRow.event.status.toLocaleUpperCase()
          )}
          {renderValues(
            'Message',
            selectedRow.event.status === 'failed'
              ? selectedRow.event.errorMessage
              : selectedRow.event.successMessage
          )}
        </Flex>

        <Divider mt={3} mb={3} />
        <Flex direction={'column'}>
          <Text textStyle={'headingItem'}>Vendor Server Information</Text>
          {renderServerInfoTable(selectedRow.event.serverInfo, [
            'private_key',
            'secret_key',
            'password',
            'access_key'
          ])}
        </Flex>
      </Flex>
    </Box>
  );
};

export default VendorExportHistoryDetail;
